/**
 * Chronological ordering for the agent sidebar's feed.
 *
 * The sidebar used to render host-supplied content (the bulk editor's
 * confirmation card, its "applied N changes" note, its failure banner) in a
 * `feedSlot` placed AFTER the message list. That made position structural rather
 * than chronological: every notice sat pinned at the bottom no matter when it
 * happened, so a note about an action three turns ago appeared to belong to the
 * newest message, and a warning stayed stuck under the conversation as it grew.
 *
 * Merging by timestamp puts each notice where it actually occurred. Messages and
 * items are concatenated in that order before sorting, and Array#sort is stable,
 * so a notice sharing a timestamp with the message that caused it lands just
 * after it — which is the reading order you want.
 *
 * Items missing a timestamp sort to the END rather than the beginning: an
 * undated notice is almost always one just produced by the host, and burying it
 * at the top of a long conversation would hide it.
 *
 * `pin: 'end'` is the stronger form of that, and it exists for the confirmation
 * card specifically. A preview arrives on the tool result, which lands BEFORE
 * the agent finishes the sentence explaining it — so by timestamp the card
 * genuinely IS older than the explanation, and sorting honestly put a Confirm
 * button above the text describing what you'd be confirming. Pinning the live
 * card to the end keeps the reading order the user needs (agent explains, then
 * asks) no matter when the event arrived.
 */

/** Undated content sorts here — after everything with a real timestamp. */
const UNDATED = Number.MAX_SAFE_INTEGER - 1;
/** `pin: 'end'` content sorts here — after undated content too. */
const PINNED_END = Number.MAX_SAFE_INTEGER;

/**
 * @param {Array} messages  session.messages — { role, text, ts, id }
 * @param {Array} feedItems host content — { key, ts, node, pin? }; falsy entries skipped
 * @returns {Array} [{ kind: 'message' | 'item', key, ts, message?, node? }]
 */
export function mergeFeed(messages, feedItems) {
  const msgs = (Array.isArray(messages) ? messages : []).map((m, i) => ({
    kind: 'message',
    key: m?.id || `${m?.role || 'msg'}-${i}`,
    ts: Number.isFinite(m?.ts) ? m.ts : UNDATED,
    message: m,
    seq: i,
  }));
  const items = (Array.isArray(feedItems) ? feedItems : [])
    .filter((it) => it && it.node)
    .map((it, i) => ({
      kind: 'item',
      key: it.key || `feed-item-${i}`,
      ts: it.pin === 'end'
        ? PINNED_END
        : (Number.isFinite(it.ts) ? it.ts : UNDATED),
      node: it.node,
      seq: msgs.length + i,
    }));

  return [...msgs, ...items].sort((a, b) => (a.ts - b.ts) || (a.seq - b.seq));
}

export default { mergeFeed };
